import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { classNames } from '@/lib/utils';
import {
  ESTADO_LABELS,
  MODALIDAD_LABELS,
  MOTIVO_DESCARTE_LABELS,
  nombreCompleto,
  type CambioEstadoCandidato,
  type Candidato,
  type EstadoCandidato,
  type ModalidadEntrevista,
  type MotivoDescarte,
} from '@/lib/candidatos';

/**
 * Mover a un candidato de estado.
 *
 * Cada destino pide lo que hace falta para que el cambio sirva de algo: un
 * descarte sin motivo no se puede explicar tres meses después, y una entrevista
 * sin fecha no genera la tarea que hace que alguien se acuerde.
 *
 * Aquí no hay ninguna casilla de «avisar por correo»: de esta sección no sale
 * ningún correo al candidato. Las plantillas existen, esperando al formulario
 * de la web (ver `server/src/services/email/candidatos.ts`).
 */
interface Props {
  candidato: Candidato;
  destino: EstadoCandidato;
  onConfirmar: (cambio: CambioEstadoCandidato) => Promise<void>;
  onCerrar: () => void;
}

/** Qué le pasa al candidato, dicho antes de pulsar. */
const CONSECUENCIAS: Record<EstadoCandidato, string> = {
  nuevo: '',
  en_bolsa: 'Vuelve a la bolsa. El plazo de conservación no cambia: solo se alarga si el candidato lo pide.',
  preseleccionado: 'Se crea una tarea para llamarle en las próximas 48 horas.',
  entrevista: 'Se crea una tarea con la fecha de la entrevista.',
  contratado: 'Sale de la bolsa y su documentación deja de caducar.',
  descartado: 'Se guarda el motivo, que es interno y no se le cuenta a nadie.',
};

export default function ModalEstado({ candidato, destino, onConfirmar, onCerrar }: Props) {
  const [nota, setNota] = useState('');
  const [motivo, setMotivo] = useState<MotivoDescarte>('perfil_no_encaja');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('10:00');
  const [lugar, setLugar] = useState('');
  const [modalidad, setModalidad] = useState<ModalidadEntrevista>('presencial');
  const [puesto, setPuesto] = useState(candidato.puestos?.[0] || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pideFecha = destino === 'entrevista' || destino === 'contratado';

  const completo = !pideFecha || !!fecha;

  const confirmar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const cambio: CambioEstadoCandidato = { estado: destino, nota: nota.trim() || undefined };

      if (destino === 'descartado') cambio.motivo = motivo;
      if (destino === 'entrevista') {
        cambio.entrevista = {
          fecha: new Date(`${fecha}T${hora || '10:00'}`).toISOString(),
          lugar: lugar.trim() || undefined,
          modalidad,
        };
      }
      if (destino === 'contratado') {
        cambio.contratacion = { fecha, puesto: puesto.trim() || undefined };
      }
      await onConfirmar(cambio);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen onClose={() => !guardando && onCerrar()} title={`Pasar a «${ESTADO_LABELS[destino]}»`}>
      <div className="space-y-4">
        <p className="text-sm text-sereno-dark/70">
          {nombreCompleto(candidato)}. {CONSECUENCIAS[destino]}
        </p>

        {destino === 'descartado' && (
          <div>
            <label className="label" htmlFor="motivo-descarte">
              Motivo *
            </label>
            <select
              id="motivo-descarte"
              className="input h-11"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value as MotivoDescarte)}
            >
              {Object.entries(MOTIVO_DESCARTE_LABELS).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {destino === 'entrevista' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label" htmlFor="fecha-entrevista">
                  Día *
                </label>
                <input
                  id="fecha-entrevista"
                  className="input h-11"
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="hora-entrevista">
                  Hora
                </label>
                <input
                  id="hora-entrevista"
                  className="input h-11"
                  type="time"
                  value={hora}
                  onChange={(e) => setHora(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="label">Modalidad</label>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(MODALIDAD_LABELS).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setModalidad(id as ModalidadEntrevista)}
                    aria-pressed={modalidad === id}
                    className={classNames(
                      'h-10 px-3 rounded-lg border text-sm font-medium transition-colors',
                      modalidad === id
                        ? 'border-sereno-dark bg-sereno-dark text-white'
                        : 'border-gray-200 bg-white text-sereno-dark/70 hover:border-sereno-dark/40',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label" htmlFor="lugar-entrevista">
                Lugar o enlace
              </label>
              <input
                id="lugar-entrevista"
                className="input h-11"
                value={lugar}
                onChange={(e) => setLugar(e.target.value)}
                placeholder="Oficina de Barbate, enlace de la videollamada…"
              />
            </div>
          </>
        )}

        {destino === 'contratado' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="fecha-alta">
                Incorporación *
              </label>
              <input
                id="fecha-alta"
                className="input h-11"
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="puesto-alta">
                Puesto
              </label>
              <input
                id="puesto-alta"
                className="input h-11"
                value={puesto}
                onChange={(e) => setPuesto(e.target.value)}
              />
            </div>
          </div>
        )}

        <div>
          <label className="label" htmlFor="nota-estado">
            Nota interna <span className="font-normal text-sereno-dark/40">(opcional)</span>
          </label>
          <textarea
            id="nota-estado"
            className="input resize-none"
            rows={3}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Por qué se toma esta decisión. No se envía al candidato."
          />
        </div>

        {error && <div className="card bg-red-50 border-red-200 text-red-700 text-sm py-3">{error}</div>}

        <div className="flex gap-2 justify-end">
          <button onClick={onCerrar} disabled={guardando} className="btn-ghost h-11 text-sm">
            Cancelar
          </button>
          <button
            onClick={() => void confirmar()}
            disabled={guardando || !completo}
            className="btn-primary h-11 text-sm flex items-center gap-2"
          >
            {guardando && <Loader2 size={16} className="animate-spin" />}
            Confirmar
          </button>
        </div>
      </div>
    </Modal>
  );
}
